"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { accent } from "@/lib/palette";

// ── Log lines ────────────────────────────────────────────────────────

type LogLine = {
  text: string;
  color?: string;
  delay: number; // ms before this line appears (after previous)
};

const LOG_LINES: LogLine[] = [
  { text: "[Qosm] Agent started — capability surface: Http(\"api.stripe.com\")", delay: 600 },
  { text: "[Qosm] Calling Stripe API v1/charges...", delay: 800 },
  { text: "[Qosm] Ok — 200", delay: 1000 },
  { text: "[Qosm] Error: field 'amount' not found in response schema", color: "#f87171", delay: 400 },
  { text: "[Qosm] Retry 2/3...", delay: 1200 },
  { text: "[Qosm] Error: field 'amount' not found in response schema", color: "#f87171", delay: 600 },
  { text: "[Qosm] Retry 3/3...", delay: 1200 },
  { text: "[Qosm] Error: field 'amount' not found in response schema", color: "#f87171", delay: 600 },
  { text: "[Qosm] Retries exhausted — self-healing triggered", color: accent.DEFAULT, delay: 800 },
  { text: "[Qosm] Fetching agent source...", delay: 600 },
  { text: "[Qosm] Generating fix via Claude Sonnet...", delay: 2400 },
  { text: "[Qosm]   ↳ Analyzing error context...", color: "var(--color-text-muted)", delay: 1200 },
  { text: "[Qosm]   ↳ Generating patch...", color: "var(--color-text-muted)", delay: 1800 },
  { text: "[Qosm]   ↳ Patch ready — 1 file changed", color: "var(--color-text-muted)", delay: 1000 },
  { text: "[Qosm]   ↳ Typechecking... ✓", color: "var(--color-text-muted)", delay: 800 },
  { text: "[Qosm]   ↳ Running tests... 27 passed ✓, 0 failing", color: "var(--color-text-muted)", delay: 1200 },
  { text: "[Qosm]   ↳ Writing regression test for current issue... 1 new test", color: "var(--color-text-muted)", delay: 1400 },
  { text: "[Qosm] Pushing healed code to dev...", delay: 800 },
  { text: "[Qosm] Agent resumed — {healed: true}", color: "#6ee7b7", delay: 600 },
];

const RESTART_DELAY = 6000;

// ── Component ────────────────────────────────────────────────────────

export default function SelfModifying() {
  const [visibleCount, setVisibleCount] = useState(0);
  const [fading, setFading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [started, setStarted] = useState(false);

  // Start animation when section scrolls into view
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStarted(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Drive the line-by-line reveal
  useEffect(() => {
    if (!started) return;
    clearTimer();

    if (visibleCount < LOG_LINES.length) {
      const nextDelay = LOG_LINES[visibleCount].delay;
      timerRef.current = setTimeout(() => {
        setVisibleCount((c) => c + 1);
      }, nextDelay);
    } else {
      // All lines shown — pause, fade, restart
      timerRef.current = setTimeout(() => {
        setFading(true);
        setTimeout(() => {
          setVisibleCount(0);
          setFading(false);
        }, 600);
      }, RESTART_DELAY);
    }

    return clearTimer;
  }, [visibleCount, started, clearTimer]);

  return (
    <section ref={sectionRef} className="py-24 px-4 max-md:py-16">
      <div className="max-w-[800px] mx-auto">
        {/* Typographic statement */}
        <div className="text-center mb-12 max-md:mb-8">
          <h2 className="text-[clamp(1.75rem,4vw,2.5rem)] font-bold text-text-primary tracking-tight mb-5">
            Agents that <span style={{ color: accent.DEFAULT }}>heal themselves</span>.
          </h2>
          <p className="text-lg text-text-secondary max-w-[640px] mx-auto leading-relaxed max-md:text-base">
            Qosm agents can <span className="text-text-primary font-medium">autonomously</span> inspect
            their own logs, fix and <span className="text-text-primary font-medium">redeploy themselves</span> when
            code or data they interface with changes. It&apos;s not something we built on purpose,
            just a consequence of Qosm&apos;s design.
          </p>
        </div>

        {/* Animated console log */}
        <div className="max-w-[700px] mx-auto">
          <div className="rounded-xl overflow-hidden border border-border">
            {/* Terminal chrome */}
            <div className="bg-bg-surface px-4 py-2.5 flex justify-between items-center border-b border-border">
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-text-muted">qosm runtime</span>
              </div>
              <span
                className="w-2 h-2 rounded-full shrink-0"
                style={{
                  backgroundColor:
                    visibleCount >= LOG_LINES.length
                      ? "#6ee7b7"
                      : visibleCount > 3
                        ? accent.DEFAULT
                        : "#6ee7b7",
                }}
              />
            </div>

            {/* Log output */}
            <div className="bg-bg-inset">
              <pre
                className={`m-0 px-5 pt-4 pb-4 text-[13px] leading-relaxed overflow-x-auto max-md:text-[11px] max-md:px-4 transition-opacity duration-500 ${
                  fading ? "opacity-0" : "opacity-100"
                }`}
                style={{ minHeight: `calc(${LOG_LINES.length} * 1.75em + 2rem)` }}
              >
                <code className="font-mono">
                  {LOG_LINES.slice(0, visibleCount).map((line, i) => (
                    <div
                      key={`${i}-${visibleCount > i ? "visible" : ""}`}
                      className="animate-fade-in-up"
                      style={{
                        color: line.color || "var(--color-text-muted)",
                        animationDuration: "0.3s",
                      }}
                    >
                      {line.text}
                    </div>
                  ))}
                </code>
              </pre>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
